import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { RamadanDay, Mosque } from "./MawaqeetRamadanPage";

// Importer lokale assets
import mosqueBackgroundImg from "../../../assets/ramadan/mosque-background.jpg";
import cityBackgroundImg from "../../../assets/ramadan/city-background.jpg";
import mawaqeetLogoImg from "../../../assets/ramadan/mawaqeet-logo.png";
import qrCodeImg from "../../../assets/ramadan/qr.png";
import aishaQrImg from "../../../assets/ramadan/aisha.png";

// Spesielle orgnr
const AISHA_ORG_NR = "980002837";

/**
 * Laster inn et bilde fra URL via Image element og canvas
 */
async function loadImageAsBase64(url: string): Promise<string | null> {
    return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = "anonymous";

        img.onload = () => {
            try {
                const canvas = document.createElement("canvas");
                canvas.width = img.naturalWidth;
                canvas.height = img.naturalHeight;
                const ctx = canvas.getContext("2d");
                if (ctx) {
                    ctx.drawImage(img, 0, 0);
                    const dataUrl = canvas.toDataURL("image/png");
                    resolve(dataUrl);
                } else {
                    resolve(null);
                }
            } catch (error) {
                console.error("Canvas error:", error);
                resolve(null);
            }
        };

        img.onerror = () => {
            console.error("Kunne ikke laste bilde:", url);
            resolve(null);
        };

        img.src = url;
    });
}

/**
 * Tegner et avrundet rektangel (kort)
 */
function drawRoundedRect(
    doc: jsPDF,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number,
    fillColor: [number, number, number]
) {
    doc.setFillColor(fillColor[0], fillColor[1], fillColor[2]);
    doc.roundedRect(x, y, width, height, radius, radius, 'F');
}

/**
 * Genererer en PDF med Ramadan-kalenderen
 */
export async function generateRamadanPDF(
    ramadanDays: RamadanDay[],
    displayName: string,
    year: number,
    isMosque: boolean = false,
    mosqueInfo?: Mosque,
    customMessage?: string
) {
    if (!ramadanDays.length) return;

    const doc = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
    });

    const pageWidth = doc.internal.pageSize.getWidth(); // 210mm
    const pageHeight = doc.internal.pageSize.getHeight(); // 297mm

    // Hent Hijri år fra første dag
    const hijriYear = ramadanDays[0]?.hijriYear || 1446;

    // Last inn alle bilder parallelt
    console.log("Laster inn bilder...");
    const backgroundUrl = isMosque ? mosqueBackgroundImg : cityBackgroundImg;

    // For moské: last inn moskélogoen, for by: mawaqeet logo
    const [backgroundBase64, mosqueLogoBase64, mawaqeetLogoBase64] = await Promise.all([
        loadImageAsBase64(backgroundUrl),
        mosqueInfo?.logo ? loadImageAsBase64(mosqueInfo.logo) : Promise.resolve(null),
        loadImageAsBase64(mawaqeetLogoImg),
    ]);

    console.log("Bakgrunn lastet:", !!backgroundBase64);
    console.log("Moské Logo URL:", mosqueInfo?.logo);
    console.log("Moské Logo lastet:", !!mosqueLogoBase64);
    console.log("Mawaqeet Logo lastet:", !!mawaqeetLogoBase64);

    // Farger basert på type
    const cardColor: [number, number, number] = isMosque ? [0, 90, 85] : [140, 100, 50];
    const headerBgColor: [number, number, number] = isMosque ? [0, 80, 75] : [120, 85, 40];
    const highlightColor: [number, number, number] = isMosque ? [0, 110, 100] : [160, 115, 55];

    // Tegn bakgrunnsbilde
    if (backgroundBase64) {
        try {
            doc.addImage(backgroundBase64, "JPEG", 0, 0, pageWidth, pageHeight);
        } catch (error) {
            console.error("Kunne ikke legge til bakgrunn:", error);
        }
    }

    // --- HEADER SEKSJON (UTEN boks, svart font) ---
    const headerY = 16;

    // År (stor) - svart font, ingen boks
    doc.setFontSize(32);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0); // Svart
    doc.text(String(year), pageWidth / 2, headerY, { align: "center" });

    // Linje under år
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.6);
    doc.line(pageWidth / 2 - 30, headerY + 3, pageWidth / 2 + 30, headerY + 3);

    // Hijri år
    doc.setFontSize(18);
    doc.text(String(hijriYear), pageWidth / 2, headerY + 12, { align: "center" });

    // "Ramadan Kalender" - Elegant serif font (Times italic)
    doc.setFontSize(34);
    doc.setFont("times", "bolditalic");
    doc.text("Ramadan Kalender", pageWidth / 2, headerY + 26, { align: "center" });

    // Navn (moské eller by) - Større
    doc.setFontSize(20);
    doc.setFont("helvetica", "bold");
    doc.text(displayName.toUpperCase(), pageWidth / 2, headerY + 38, { align: "center" });

    // --- TABELL SEKSJON (flyttet lenger ned) ---
    const tableStartY = headerY + 52;
    const tableWidth = 115;
    const tableMarginLeft = 8;

    // Forbered tabell-data (UTEN ISHA)
    const tableData = ramadanDays.map((day) => [
        day.day.toString(),
        day.weekdayFull,
        day.formattedDate,
        day.suhur,
        day.fajr,
        day.iftar,
    ]);

    // Kolonneoverskrifter
    const headers = [["RAMADAN", "DAG", "DATO", "SUHUR SLUTT", "FAJR", "IFTAR"]];

    // Markér fredag-rader
    const fridayIndices = ramadanDays
        .map((day, idx) => (day.isFriday ? idx : -1))
        .filter((idx) => idx >= 0);

    // Generer tabell - større font og høyere rader
    autoTable(doc, {
        head: headers,
        body: tableData,
        startY: tableStartY,
        tableWidth: tableWidth,
        margin: { left: tableMarginLeft },
        styles: {
            fontSize: 8, // Litt større font
            cellPadding: 1.8, // Høyere rader
            textColor: [0, 0, 0],
            lineColor: [150, 150, 150],
            lineWidth: 0.2,
            halign: "center",
            valign: "middle",
        },
        headStyles: {
            fillColor: headerBgColor,
            textColor: [255, 255, 255],
            fontStyle: "bold",
            fontSize: 7, // Litt større header
            halign: "center",
        },
        alternateRowStyles: {
            fillColor: [255, 255, 255],
        },
        bodyStyles: {
            fillColor: [250, 250, 250],
            fontStyle: "bold", // Bold tekst i tabellen
        },
        columnStyles: {
            0: { cellWidth: 17 }, // RAMADAN
            1: { cellWidth: 20 }, // DAG
            2: { cellWidth: 17 }, // DATO
            3: { cellWidth: 19 }, // SUHUR
            4: { cellWidth: 19 }, // FAJR
            5: { cellWidth: 19 }, // IFTAR
        },
        didParseCell: (data: any) => {
            // Fremhev fredag-rader
            if (data.section === "body" && fridayIndices.includes(data.row.index)) {
                data.cell.styles.fillColor = highlightColor;
                data.cell.styles.textColor = [255, 255, 255];
                data.cell.styles.fontStyle = "bold";
            }
        },
    });

    // --- HØYRE SIDE INFO-PANEL ---
    const rightPanelX = tableMarginLeft + tableWidth + 5;
    const rightPanelWidth = pageWidth - rightPanelX - 8;
    const cardRadius = 4;
    let rightY = tableStartY;

    // --- KORT 1: Mawaqeet info-tekst (høyere boks, større tekst) ---
    doc.setFontSize(10); // Større font
    doc.setFont("helvetica", "normal");

    const infoText = "Mawaqeet utarbeider bønnetider for muslimer i Norge. Bønnetabeller laget basert på læren om Fiqh og lokal astronomi ifølge forståelsen til klassiske lærde";
    const infoLines = doc.splitTextToSize(infoText, rightPanelWidth - 6);
    const lineHeight = 5.5; // Litt høyere linjer
    const card1Height = infoLines.length * lineHeight + 16; // Høyere boks

    drawRoundedRect(doc, rightPanelX, rightY, rightPanelWidth, card1Height, cardRadius, cardColor);

    doc.setTextColor(255, 255, 255);
    infoLines.forEach((line: string, idx: number) => {
        doc.text(line, rightPanelX + rightPanelWidth / 2, rightY + 10 + idx * lineHeight, { align: "center" });
    });
    rightY += card1Height + 4; // Mindre padding

    // --- KORT 2: iBønn app med QR-kode - Mørk blå #043b51 ---
    const card2Height = 55;
    const ibonnCardColor: [number, number, number] = [4, 59, 81]; // #043b51
    drawRoundedRect(doc, rightPanelX, rightY, rightPanelWidth, card2Height, cardRadius, ibonnCardColor);

    // "Last ned iBønn" tekst øverst
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(255, 255, 255);
    doc.text("Last ned iBønn", rightPanelX + rightPanelWidth / 2, rightY + 8, { align: "center" });

    // QR-kode i midten
    const qrCodeBase64 = await loadImageAsBase64(qrCodeImg);
    if (qrCodeBase64) {
        try {
            const qrSize = 30; // Skalert størrelse
            const qrX = rightPanelX + (rightPanelWidth - qrSize) / 2;
            doc.addImage(qrCodeBase64, "PNG", qrX, rightY + 12, qrSize, qrSize);
        } catch (e) {
            console.error("Kunne ikke legge til QR-kode:", e);
        }
    }

    // "Få alle Mawaqeet tidene på din mobil" tekst nederst
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text("Få alle Mawaqeet tidene på din mobil", rightPanelX + rightPanelWidth / 2, rightY + 50, { align: "center" });

    rightY += card2Height + 4; // Mindre padding

    // --- KORT 3: Sadaqat-ul-Fitr ---
    const card3Height = 14;
    drawRoundedRect(doc, rightPanelX, rightY, rightPanelWidth, card3Height, cardRadius, [180, 50, 50]);

    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(255, 255, 255);
    doc.text("HUSK Å GI SADAQAT-UL-FITR", rightPanelX + rightPanelWidth / 2, rightY + 9, { align: "center" });
    rightY += card3Height + 4; // Veldig lite padding etter Sadaqat

    // --- SPESIELL HÅNDTERING FOR MASJID AISHA (980002837) ---
    const isAisha = mosqueInfo?.orgNr === AISHA_ORG_NR;

    if (isAisha) {
        // QR-kode boks for Aisha
        const aishaBoxHeight = 55;
        drawRoundedRect(doc, rightPanelX, rightY, rightPanelWidth, aishaBoxHeight, cardRadius, cardColor);

        // Tekst øverst
        doc.setFontSize(8);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(255, 255, 255);
        doc.text("Skann QR koden for å bli medlem,", rightPanelX + rightPanelWidth / 2, rightY + 8, { align: "center" });
        doc.text("eller laste ned vår app.", rightPanelX + rightPanelWidth / 2, rightY + 14, { align: "center" });

        // Aisha QR-kode
        const aishaQrBase64 = await loadImageAsBase64(aishaQrImg);
        if (aishaQrBase64) {
            try {
                const qrSize = 32;
                const qrX = rightPanelX + (rightPanelWidth - qrSize) / 2;
                doc.addImage(aishaQrBase64, "PNG", qrX, rightY + 18, qrSize, qrSize);
            } catch (e) {
                console.error("Kunne ikke legge til Aisha QR:", e);
            }
        }
        rightY += aishaBoxHeight + 6;

        // Egendefinert melding (under QR for Aisha)
        if (customMessage && customMessage.length > 0) {
            doc.setFontSize(9);
            doc.setFont("helvetica", "bold");
            const messageLines = doc.splitTextToSize(customMessage, rightPanelWidth - 6);
            const messageBoxHeight = messageLines.length * 5 + 10;

            drawRoundedRect(doc, rightPanelX, rightY, rightPanelWidth, messageBoxHeight, cardRadius, cardColor);

            doc.setTextColor(255, 255, 255);
            messageLines.forEach((line: string, idx: number) => {
                doc.text(line, rightPanelX + rightPanelWidth / 2, rightY + 7 + idx * 5, { align: "center" });
            });
            rightY += messageBoxHeight + 6;
        }
    } else {
        // --- EGENDEFINERT MELDING (over logo) - for andre moskeer/byer ---
        if (customMessage && customMessage.length > 0) {
            doc.setFontSize(9);
            doc.setFont("helvetica", "bold");
            const messageLines = doc.splitTextToSize(customMessage, rightPanelWidth - 6);
            const messageBoxHeight = messageLines.length * 5 + 10;

            drawRoundedRect(doc, rightPanelX, rightY, rightPanelWidth, messageBoxHeight, cardRadius, cardColor);

            doc.setTextColor(255, 255, 255);
            messageLines.forEach((line: string, idx: number) => {
                doc.text(line, rightPanelX + rightPanelWidth / 2, rightY + 7 + idx * 5, { align: "center" });
            });
            rightY += messageBoxHeight + 6;
        }
    }

    // --- Logo: MOSKÉ LOGO for moské, MAWAQEET for by ---
    let displayLogo: string | null = null;
    if (isMosque) {
        displayLogo = mosqueLogoBase64;
        console.log("Bruker moské logo:", !!displayLogo);
    } else {
        displayLogo = mawaqeetLogoBase64;
    }

    if (displayLogo) {
        try {
            // Bruk en annen metode for å få riktige dimensjoner
            // Lag et temporært bilde for å hente størrelsen
            const tempImg = document.createElement('img');
            tempImg.src = displayLogo;

            // Vent på at bildet lastes for å få riktige dimensjoner
            await new Promise<void>((resolve) => {
                tempImg.onload = () => resolve();
                tempImg.onerror = () => resolve();
                // Timeout etter 500ms
                setTimeout(() => resolve(), 500);
            });

            // Beregn aspect ratio - maks 50x55, men behold proporsjoner (litt mindre)
            const maxWidth = 50;
            const maxHeight = 55;
            let logoWidth = maxWidth;
            let logoHeight = maxHeight;

            if (tempImg.naturalWidth && tempImg.naturalHeight) {
                const aspectRatio = tempImg.naturalWidth / tempImg.naturalHeight;
                if (aspectRatio > (maxWidth / maxHeight)) {
                    // Bredere - begrens på bredde
                    logoWidth = maxWidth;
                    logoHeight = maxWidth / aspectRatio;
                } else {
                    // Høyere - begrens på høyde
                    logoHeight = maxHeight;
                    logoWidth = maxHeight * aspectRatio;
                }
            }

            const logoX = rightPanelX + (rightPanelWidth - logoWidth) / 2;
            doc.addImage(displayLogo, "PNG", logoX, rightY, logoWidth, logoHeight);
            rightY += logoHeight + 2; // Mindre mellomrom til adresse
        } catch (error) {
            console.error("Kunne ikke legge til logo:", error);
            rightY += 4;
        }
    } else if (isMosque) {
        rightY += 4;
    }

    // --- Moské: Adresse og kontaktinfo (UTEN boks, svart tekst, nærmere logo) ---
    if (isMosque && mosqueInfo) {
        const hasAddress = mosqueInfo.streetAddress || mosqueInfo.city;
        const hasContact = mosqueInfo.contactPhone;

        let textY = rightY + 2; // Nærmere logoen
        doc.setTextColor(0, 0, 0); // Svart tekst

        if (hasAddress) {
            doc.setFontSize(9);
            doc.setFont("helvetica", "bold");
            doc.text(mosqueInfo.streetAddress || "", rightPanelX + rightPanelWidth / 2, textY, { align: "center" });
            textY += 5;
            doc.setFont("helvetica", "normal");
            doc.text(`${mosqueInfo.postalCode || ""}, ${mosqueInfo.city?.toUpperCase() || ""}`, rightPanelX + rightPanelWidth / 2, textY, { align: "center" });
            textY += 5; // Mindre mellomrom til kontaktinfo
        }

        if (hasContact) {
            doc.setFontSize(8);
            doc.setFont("helvetica", "bold");
            doc.text("Kontaktinformasjon:", rightPanelX + rightPanelWidth / 2, textY, { align: "center" });
            textY += 4;
            doc.setFont("helvetica", "normal");
            doc.text(mosqueInfo.contactPhone || "", rightPanelX + rightPanelWidth / 2, textY, { align: "center" });
        }
    } else if (!isMosque) {
        // By: Mawaqeet website info
        const card4Height = 22;
        drawRoundedRect(doc, rightPanelX, rightY, rightPanelWidth, card4Height, cardRadius, cardColor);

        doc.setFontSize(7);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(255, 255, 255);
        doc.text("FOR UTDYPENDE ARTIKLER OG VIDEOER, BESØK", rightPanelX + rightPanelWidth / 2, rightY + 8, { align: "center" });
        doc.setFontSize(11);
        doc.setFont("helvetica", "bold");
        doc.text("WWW.MAWAQEET.NO", rightPanelX + rightPanelWidth / 2, rightY + 16, { align: "center" });
    }

    // --- FOOTER ---
    doc.setFontSize(8);
    doc.setFont("helvetica", "italic");
    doc.setTextColor(50, 50, 50);
    const footerText = "Laget av iBønn - Mawaqeet - Bønnetider i Norge";
    doc.text(footerText, pageWidth / 2, pageHeight - 6, { align: "center" });

    // Last ned PDF
    doc.save(`Ramadan ${year} - ${displayName}.pdf`);
}
